/**
 * 权限溯源抽屉组件
 * 展示权限在4层引擎中的评估路径和最终结果
 */
import React, { useState } from 'react';
import {
  Drawer, Form, Input, Button, Steps, Tag, Card,
  Space, Descriptions, Result, Spin, Alert,
} from 'antd';
import {
  SearchOutlined, CheckCircleOutlined, CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { tracePermission } from '../../api/permissionEngine';
import type { TracePermissionResult, TraceLayerResult } from '../../api/types';

interface PermissionTraceDrawerProps {
  open: boolean;
  onClose: () => void;
  defaultUserId?: number;
  defaultPermissionCode?: string;
}

const layerLabels: Record<string, string> = {
  role: '角色权限层',
  override: '权限覆盖层',
  data_scope: '数据范围层',
  field: '字段权限层',
  base: '基础权限层',
  template: '模板权限层',
};

const layerDescriptions: Record<string, string> = {
  role: '检查用户所属角色是否拥有该权限',
  override: '检查是否有针对用户的个性化权限覆盖',
  data_scope: '检查数据范围限制',
  field: '检查字段级权限',
  base: '基础权限检查',
  template: '角色模板权限检查',
};

const getLayerIcon = (layer: TraceLayerResult) => {
  if (!layer.checked) return <MinusCircleOutlined style={{ color: '#d9d9d9' }} />;
  if (layer.result === 'allow' || layer.result === 'grant') {
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  }
  if (layer.result === 'deny') {
    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  }
  return <MinusCircleOutlined style={{ color: '#faad14' }} />;
};

const getLayerStatus = (layer: TraceLayerResult): 'finish' | 'error' | 'wait' | 'process' => {
  if (!layer.checked) return 'wait';
  if (layer.result === 'allow' || layer.result === 'grant') return 'finish';
  if (layer.result === 'deny') return 'error';
  return 'process';
};

const PermissionTraceDrawer: React.FC<PermissionTraceDrawerProps> = ({
  open,
  onClose,
  defaultUserId,
  defaultPermissionCode,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [traceResult, setTraceResult] = useState<TracePermissionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTrace = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      setError(null);
      setTraceResult(null);

      const res = await tracePermission({
        userId: Number(values.userId),
        permissionCode: values.permissionCode,
      });

      if (res?.data) {
        setTraceResult(res.data as any);
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      setError(err?.message || '溯源查询失败');
      console.error('权限溯源失败', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTraceResult(null);
    setError(null);
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title="权限溯源"
      open={open}
      onClose={handleClose}
      width={600}
      destroyOnHidden
    >
      {/* 查询表单 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          initialValues={{
            userId: defaultUserId,
            permissionCode: defaultPermissionCode,
          }}
          onFinish={handleTrace}
        >
          <Form.Item
            name="userId"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input placeholder="用户ID" style={{ width: 120 }} />
          </Form.Item>
          <Form.Item
            name="permissionCode"
            rules={[{ required: true, message: '请输入权限码' }]}
          >
            <Input placeholder="权限码" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              htmlType="submit"
              loading={loading}
            >
              溯源
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 加载状态 */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" tip="正在溯源权限评估路径..."><div style={{ minHeight: 100 }} /></Spin>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <Alert
          type="error"
          message="溯源失败"
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 溯源结果 */}
      {traceResult && !loading && (
        <>
          {/* 最终结果 */}
          <Result
            status={traceResult.finalResult.allowed ? 'success' : 'error'}
            title={traceResult.finalResult.allowed ? '权限允许' : '权限拒绝'}
            subTitle={
              <Space direction="vertical" size={4}>
                <span>权限码: {traceResult.permissionCode}</span>
                <span>来源: {traceResult.finalResult.source}</span>
                {traceResult.finalResult.reason && (
                  <span>原因: {traceResult.finalResult.reason}</span>
                )}
              </Space>
            }
            style={{ padding: '16px 0' }}
          />

          {/* 基本信息 */}
          <Descriptions
            size="small"
            bordered
            column={2}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="用户ID">{traceResult.userId}</Descriptions.Item>
            <Descriptions.Item label="租户ID">{traceResult.tenantId}</Descriptions.Item>
            <Descriptions.Item label="权限码" span={2}>
              <code>{traceResult.permissionCode}</code>
            </Descriptions.Item>
          </Descriptions>

          {/* 各层评估结果 */}
          <Card title="评估路径" size="small">
            <Steps
              direction="vertical"
              size="small"
              items={traceResult.layers.map((layer) => ({
                title: (
                  <Space>
                    <span>{layerLabels[layer.layer] || layer.layer}</span>
                    {layer.checked ? (
                      <Tag
                        color={
                          layer.result === 'allow' || layer.result === 'grant'
                            ? 'success'
                            : layer.result === 'deny'
                            ? 'error'
                            : 'warning'
                        }
                      >
                        {layer.result}
                      </Tag>
                    ) : (
                      <Tag>未检查</Tag>
                    )}
                  </Space>
                ),
                description: (
                  <div style={{ fontSize: 12 }}>
                    <div style={{ color: '#999' }}>
                      {layerDescriptions[layer.layer] || ''}
                    </div>
                    {layer.detail && (
                      <div style={{ marginTop: 4 }}>{layer.detail}</div>
                    )}
                  </div>
                ),
                status: getLayerStatus(layer),
                icon: getLayerIcon(layer),
              }))}
            />
          </Card>
        </>
      )}

      {/* 空状态 */}
      {!traceResult && !loading && !error && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          输入用户ID和权限码，点击溯源查看权限评估路径
        </div>
      )}
    </Drawer>
  );
};

export default PermissionTraceDrawer;
